/**
 * PoolDetailPage - Phase 3H-U Redesign
 * 
 * Single pool detail view.
 * Uses actual component interfaces from JoinPoolModal and LeavePoolModal.
 * 
 * UPDATED: Removed leaderboard prop - PoolDetails fetches its own stats now
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePool } from '../hooks/usePool';
import { usePoolMembership } from '../hooks';
import { useJoinPool } from '../hooks/useJoinPool';
import { LoadingSpinner, Alert, Button } from '../components/common';
import { PoolDetails } from '../components/pools/PoolDetails';
import { JoinPoolModal } from '../components/pools/JoinPoolModal';

export function PoolDetailPage(): JSX.Element {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  
  const safePoolId = poolId || '';
  
  const { pool, loading: poolLoading, error: poolError, refetch: refetchPool } = usePool(safePoolId);
  const { membership, refresh: refreshMembership } = usePoolMembership(safePoolId);
  
  const { joinPool, loading: joining, error: joinError, reset: resetJoin } = useJoinPool();
  
  const [showJoinModal, setShowJoinModal] = useState(false);

  const handleJoin = async () => {
    if (!poolId) return;
    resetJoin();
    const result = await joinPool(poolId);
    if (result) {
      setShowJoinModal(false);
      refetchPool();
      refreshMembership();
    }
  };

  // Loading state
  if (poolLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (poolError || !pool) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="error">
          {poolError || 'Pool not found'}
        </Alert>
        <div className="mt-4">
          <Button variant="secondary" onClick={() => navigate('/pools')}>
            Back to Pools
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/app')}>
          ← Back to Dashboard
        </Button>
      </div>

      {/* Error Messages */}
      {joinError && (
        <Alert variant="error" className="mb-6" onClose={resetJoin}>
          {joinError}
        </Alert>
      )}

      {/* Pool Details */}
      <PoolDetails
        pool={pool}
        membership={membership}
        onJoin={() => setShowJoinModal(true)}
      />

      {/* Join Modal */}
      <JoinPoolModal
        pool={pool}
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onConfirm={handleJoin}
        loading={joining}
        error={joinError}
      />
    </div>
  );
}
